<template>
  <Layout padding-horizontal="0" padding-vertical="0">
    <CompanyHeader
        :company-id="details.companyId"
        :is-loading="isLoading"
        :title="details.name"
        :status="details.status"
        :user-count="details.totalUsers"
        :program-count="details.totalProgram"
        :created-at="details.createdAt"
        :photo="details.avatarUrl"
        :invite-code="details.inviteCode"
        :on-edit="showEditModal"
        :on-delete="showDeleteModal"
    />
    <div class="main-content">
      <div class="tab-panel-row">
        <div class="tab-panel">
          <button class="tab-button" :class="{'active': tabIndex === 0}" @click="() => changeTab(0)">
            <span>{{ $t('general_tab') }}</span>
          </button>
          <button class="tab-button" :class="{'active': tabIndex === 1}" @click="() => changeTab(1)">
            <span>{{ $t('users_tab') }}</span>
          </button>
          <button class="tab-button" :class="{'active': tabIndex === 2}" @click="() => changeTab(2)">
            <span>{{ $t('programs_tab') }}</span>
          </button>
          <button class="tab-button" :class="{'active': tabIndex === 3}" @click="() => changeTab(3)">
            <span>{{ $t('tests_tab') }}</span>
          </button>
          <button class="tab-button" :class="{'active': tabIndex === 5}" @click="() => changeTab(5)">
            <span>{{ $t('game_labs_tab') }}</span>
          </button>
          <button class="tab-button" :class="{'active': tabIndex === 6}" @click="() => changeTab(6)">
            <span>{{ $t('departments') }}</span>
          </button>
          <button class="tab-button" :class="{'active': tabIndex === 7}" @click="() => changeTab(7)">
            <span>{{ $t('license_title') }}</span>
          </button>
          <button class="tab-button" :class="{'active': tabIndex === 8}" @click="() => changeTab(8)">
            <span>{{ $t('remained_usages') }}</span>
          </button>
        </div>

        <div class="tab-actions">
          <div class="action-row" v-if="tabIndex === 1">
            <ExpandableSearch :value="searchText" @input="handleSearch" :cancel="handleSearch" />

            <div class="btn-group-assign" @click="showGroupAssignModal">
              <img src="../assets/images/plus.svg">
              <span>{{$t('group_assign')}}</span>
            </div>

            <div class="justify-end-add-btn no-text-select" @click="showInviteUser">
              <img src="../assets/images/plus.svg">
              <span>{{$t('add_new')}}</span>
            </div>
          </div>
          <div class="action-row" v-if="tabIndex === 2">
            <button
                :class="deleteSure ? 'delete-button sure' : 'delete-button'"
                @click="handleMultipleDelete"
                :disabled="selectedPrograms.length === 0"
            >
              {{ deleteSure ? $t('are_you_sure') : $t('remove') }}
              <span v-if="selectedPrograms.length > 0">({{ selectedPrograms.length }})</span>
            </button>
            <ExpandableSearch :value="searchText" @input="handleSearch" :cancel="handleSearch" />
            <button class="dark-button-sm" @click="openAssignModal">{{$t('assign_new')}}</button>
          </div>

          <div class="action-row" v-if="tabIndex === 3">
            <button
                :class="deleteTestSure ? 'delete-button sure' : 'delete-button'"
                @click="handleMultipleTestDelete"
                :disabled="selectedTests.length === 0"
            >
              {{ deleteTestSure ? $t('are_you_sure') : $t('remove') }}
              <span v-if="selectedTests.length > 0">({{ selectedTests.length }})</span>
            </button>
            <ExpandableSearch :value="searchText" @input="handleSearch" :cancel="handleSearch" />
            <button class="dark-button-sm" @click="showAssignTestModal">{{$t('assign_new')}}</button>
          </div>

          <div class="action-row" v-if="tabIndex === 5">
            <button
                :class="deleteGameLabSure ? 'delete-button sure' : 'delete-button'"
                @click="handleMultipleGameLabDelete"
                :disabled="selectedGameLabs.length === 0"
            >
              {{ deleteGameLabSure ? $t('are_you_sure') : $t('remove') }}
              <span v-if="selectedGameLabs.length > 0">({{ selectedGameLabs.length }})</span>
            </button>
            <ExpandableSearch :value="searchText" @input="handleSearch" :cancel="handleSearch"  />
            <button class="dark-button-sm" @click="showAssignGameLabsModal">{{$t('assign_new')}}</button>
          </div>

          <div class="action-row" v-if="tabIndex === 4">
            <ExpandableSearch :value="searchText"   />
            <button class="dark-button-sm" @click="showAssignGameLabsModal">{{$t('assign_new')}}</button>
          </div>
          <div class="action-row" v-if="tabIndex === 6">
            <ExpandableSearch :value="searchText" @input="handleSearch" :cancel="handleSearch" />
            <button class="dark-button-sm" @click="showDepModal">{{$t('add_department')}}</button>
          </div>
          <div class="action-row" v-if="tabIndex === 7">
            <button class="dark-button-sm" @click="showLicenseModal">{{$t('license_added')}}</button>
          </div>
        </div>
      </div>

      <div v-if="tabIndex === 0" class="tab-container">
        <h3>{{ $t('company_details') }}</h3>
        <div class="company-details">
          <div class="company-details-grid">

            <div class="company-detail-row">
              <div class="company-detail-icon">
                <img src="@/assets/images/buildings.svg" />
              </div>
              <div class="company-detail-body">
                <span class="detail-title">
                  {{ $t('company_name') }}
                </span>
                <span class="detail-text" v-if="!isLoading">
                  {{details.name}}
                </span>

                <free-style-shimmer
                    :is-loading="isLoading"
                    height="7px"
                    width="120px"
                    border-radius="50px"
                    color="#EAECF0"
                />
              </div>
            </div>

            <div class="company-detail-row">
              <div class="company-detail-icon">
                <img src="@/assets/images/calendar.svg" />
              </div>
              <div class="company-detail-body">
                <span class="detail-title">
                  {{ $t('create_date') }}
                </span>
                <span class="detail-text" v-if="!isLoading">
                  {{formatDate(details.createdAt)}}
                </span>

                <free-style-shimmer
                    :is-loading="isLoading"
                    height="7px"
                    width="120px"
                    border-radius="50px"
                    color="#EAECF0"
                />
              </div>
            </div>

            <div class="company-detail-row">
              <div class="company-detail-icon">
                <img src="@/assets/images/message.svg" />
              </div>
              <div class="company-detail-body">
                <span class="detail-title">
                 {{ $t('email_address') }}
                </span>
                <span class="detail-text" v-if="!isLoading">
                  {{details.authorizedemail}}
                </span>

                <free-style-shimmer
                    :is-loading="isLoading"
                    height="7px"
                    width="120px"
                    border-radius="50px"
                    color="#EAECF0"
                />
              </div>
            </div>

            <div class="company-detail-row">
              <div class="company-detail-icon">
                <img src="@/assets/images/phone.svg" />
              </div>
              <div class="company-detail-body">
                <span class="detail-title">
                  {{ $t('phone') }}
                </span>
                <span class="detail-text" v-if="!isLoading">
                  {{details.phone}}
                </span>

                <free-style-shimmer
                    :is-loading="isLoading"
                    height="7px"
                    width="120px"
                    border-radius="50px"
                    color="#EAECF0"
                />
              </div>
            </div>

            <div class="company-detail-row">
              <div class="company-detail-icon">
                <img src="@/assets/images/document.svg" />
              </div>
              <div class="company-detail-body">
                <span class="detail-title">
                  {{ $t('tax_id_number') }}
                </span>
                <span class="detail-text" v-if="!isLoading">
                  {{details.taxNumber}}
                </span>

                <free-style-shimmer
                    :is-loading="isLoading"
                    height="7px"
                    width="120px"
                    border-radius="50px"
                    color="#EAECF0"
                />
              </div>
            </div>

            <div class="company-detail-row">
              <div class="company-detail-icon">
                <img src="@/assets/images/dashboard.svg" />
              </div>
              <div class="company-detail-body">
                <span class="detail-title">
                  {{ $t('tax_office') }}
                </span>
                <span class="detail-text" v-if="!isLoading">
                  {{details.taxOffice}}
                </span>

                <free-style-shimmer
                    :is-loading="isLoading"
                    height="7px"
                    width="120px"
                    border-radius="50px"
                    color="#EAECF0"
                />
              </div>
            </div>

            <div class="company-detail-row">
              <div class="company-detail-icon">
                <img src="@/assets/images/address.svg" />
              </div>
              <div class="company-detail-body">
                <span class="detail-title">
                  {{ $t('address') }}
                </span>
                <span class="detail-text" v-if="!isLoading">
                  {{details.address}}
                </span>

                <free-style-shimmer
                    :is-loading="isLoading"
                    height="7px"
                    width="120px"
                    border-radius="50px"
                    color="#EAECF0"
                />
              </div>
            </div>
            <div class="company-detail-row">
              <div class="company-detail-icon">
                <img src="@/assets/images/sectorIcon.svg" />
              </div>
              <div class="company-detail-body">
                <span class="detail-title">
                  {{ $t('sector') }}
                </span>
                <span class="detail-text" v-if="!isLoading">
                  {{details.sector?.sectorName}}
                </span>

                <free-style-shimmer
                    :is-loading="isLoading"
                    height="7px"
                    width="120px"
                    border-radius="50px"
                    color="#EAECF0"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="tabIndex === 1">
        <div class="table-download-holder">
          <TableDownload
              :data="tableUsers"
              :fields="['name', 'surname', 'status', 'addedDate', 'programsCount', 'email', 'phoneNumber', 'department.name']"
              :headers="{
            name: $t('name_payment'),
            surname: $t('surname_person'),
            status: $t('status_payment_title'),
            addedDate: $t('added_date'),
            programsCount: $t('total_programs_tab'),
            email: $t('email_address'),
            phoneNumber: $t('phone_number_person'),
            'department.name': $t('user_department_name')
            }"
              :filename="$t('user_table_report')"
          />
        </div>
        <UserTable :loading="isLoading" :users="tableUsers" :right-action="false" :company-id="details.companyId" :code="details.inviteCode"/>
        <div class="pagination-align-center">
          <Pagination
              :current-page="usersCurrentPage"
              :total-items="totalUsers"
              :per-page="20"
              :on-page-change="handleUsersPage"
          />
        </div>

        <InviteUserModal
            :company-id="details.companyId"
            :role-list="roleList"
            :visible="addUserModal"
            :on-hide="hideInviteUser"
            :deps="deps"
            @user-added="handleUserAdded"
        />
        <GroupAssignModal :company-id="details.companyId" :role-list="roleList" :visible="groupAssignModal" :on-hide="hideGroupAssignModal" :deps="deps" :size="70"/>
      </div>

      <div v-if="tabIndex === 2" class="tab-container">
        <h3>{{ $t('assigned_programs') }}</h3>

        <div class="programs-row" v-if="isLoading">
          <ProgramCard :is-loading="true" />
          <ProgramCard :is-loading="true" />
          <ProgramCard :is-loading="true" />
          <ProgramCard :is-loading="true" />
        </div>

        <div class="program-page-container" v-else>
          <div class="programs-row">
            <ProgramCard
                v-for="program in programList"
                :key="program.programId"
                :image="program.avatar"
                :is-loading="false"
                :title="program.name"
                :description="program.shortDescription"
                :tags="program.tags.map((x) => x.name)"
                :remaining-days="program.remainingDay"
                :assigned-at="program.assignedAt"
                :remaining="program.remaining"
                :start-at="program.startAt"
                :finish-at="program.finishAt"
                :is-publish="program.isPublish"
                :show-delete="true"
                :companies-assigned-count="program.companiesAssignedCount"
                :user-assigned-count="program.userAssignedCount"
                :is-selected="isProgramSelected(program.programId)"
                :on-select="() => handleProgramSelect(program.programId)"
                :can-delete="program.canDelete"
                :assigned-type="program.assignedType || program.assignType"
                :on-click="() => handleProgramCardClick(program)"
            />
          </div>
        </div>

        <div v-if="!isLoading && programList.length === 0" class="no-tests-wrapper">
          <img src="../assets/images/noTests.png" class="no-tests-image">
          <span class="no-tests-text">{{$t('not_assigned_programs_yet')}}</span>
          <span class="no-tests-description">{{$t('first_program_assign')}}</span>
        </div>

        <div class="bottom-placement-pagination">
          <Pagination
              :current-page="programsCurrentPage"
              :total-items="totalPrograms"
              :per-page="9"
              :on-page-change="handleProgramsPage"
          />
        </div>
      </div>

      <div v-if="tabIndex === 3" class="tab-container">
        <h3>{{ $t('assigned_tests') }}</h3>

        <div v-if="!isLoading && testList.length === 0" class="no-tests-wrapper">
          <img src="../assets/images/noTests.png" class="no-tests-image">
          <span class="no-tests-text">{{ $t('not_assigned_tests_yet') }}</span>
          <span class="no-tests-description">{{  $t('first_test_assign') }}</span>
        </div>
        <div class="program-page-container" v-else>
          <div class="tests-grid">
            <div v-for="item in testList" v-if="!isLoading" :key="item.testId" class="test-card-wrapper">
              <div
                  class="test-card no-text-select"
                  :class="{
                  'selected': isTestSelected(item.testId),
                  'disabled-card': !item.canDelete
                  }"
                  @click="handleTestCardClick(item)"
              >

                <div
                    class="checkbox-wrapper"
                    :class="{'disabled': !item.canDelete}"
                    @click.stop="handleTestSelect(item.testId)"
                >
                  <img
                      v-if="!isTestSelected(item.testId)"
                      src="@/assets/images/checkbox-circle.svg"
                      alt="Select"
                      class="checkbox-icon"
                  />
                  <img
                      v-else
                      src="@/assets/images/checkboxchecked-circle.svg"
                      alt="Selected"
                      class="checkbox-icon"
                  />
                </div>

                <div class="status-tag" :class="item.isPublish ? 'Published' : 'Draft'">
                  <div class="status-dot" :class="item.isPublish ? 'Published' : 'Draft'"></div>
                  <span>{{item.isPublish ? $t('published') : $t('draft')}}</span>
                </div>

                <span class="test-date">{{formatDate(item.created_at)}}</span>
                <span class="test-title">{{item.name}}</span>

                <div class="test-info">
                  <template v-if="item.startAt || item.finishAt">
                    <img src="@/assets/images/datepicker.svg" class="test-info-icon"/>
                    <span class="test-info-text">
                      {{ formatDate(item.startAt) || '-' }} - {{ formatDate(item.finishAt) || '-' }}
                    </span>
                    <div class="dot"></div>
                  </template>

                  <div class="test-time-row" v-if="item.finishAt && item.remaining && item.remaining.isPast === 0">
                    <img
                        src="@/assets/images/clockOutlineRed.svg"
                        class="test-info-icon"
                    />
                    <span class="test-info-text red">
                     {{ item.remaining.time }}
                     {{$t('time_is_up')}}
                    </span>
                  </div>

                  <div class="test-time-row" v-else-if="item.finishAt && item.remaining && item.remaining.isPast === 1">
                    <img
                        src="@/assets/images/clockOutlineGreen.svg"
                        class="test-info-icon"
                    />
                    <span class="test-info-text green">
                     {{ item.remaining.time }}
                    </span>
                  </div>

                  <div class="test-time-row" v-else-if="item.finishAt && item.remaining && item.remaining.isPast === 2">
                    <img
                        src="@/assets/images/clockOutlineYellow.svg"
                        class="test-info-icon"
                    />
                    <span class="test-info-text yellow">
                      {{ item.remaining.time }}
                    </span>
                  </div>
                </div>

                <div class="info-row">
                  <span class="info-tag">{{$t('companies_count', {companiesCount: item.compainesCount})}}</span>
                  <span class="info-tag">{{$t('question_count', {questionCount: item.questionsCount})}}</span>
                </div>
              </div>
            </div>

            <free-style-shimmer
                :is-loading="isLoading"
                height="200px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />

            <free-style-shimmer
                :is-loading="isLoading"
                height="200px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />

            <free-style-shimmer
                :is-loading="isLoading"
                height="200px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />

          </div>
        </div>

        <div class="bottom-placement-pagination">
          <Pagination
              :current-page="testsCurrentPage"
              :total-items="totalTests"
              :per-page="9"
              :on-page-change="handleTestsPage"
          />
        </div>
      </div>

      <div v-if="tabIndex === 4" class="tab-container">
        <h3>{{$t('all_transactions')}}</h3>

        <TransactionTable />
      </div>

      <div v-if="tabIndex === 5" class="tab-container">
        <h3>{{ $t('assigned_game_labs') }}</h3>

        <div v-if="!isLoading && gameLabsList.length === 0" class="no-tests-wrapper">
          <img src="../assets/images/noTests.png" class="no-tests-image">
          <span class="no-tests-text">{{ $t('not_assigned_game_labs_yet') }}</span>
          <span class="no-tests-description">{{ $t('click_on_ata') }}</span>
        </div>

        <div class="program-page-container">
          <div class="gameLabs-grid">
            <free-style-shimmer
                :is-loading="isLoading"
                height="200px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />

            <free-style-shimmer
                :is-loading="isLoading"
                height="200px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />

            <free-style-shimmer
                :is-loading="isLoading"
                height="200px"
                width="100%"
                border-radius="20px"
                color="#EAECF0"
            />

            <div v-for="item in gameLabsList" v-if="!isLoading" :key="item.gameLabsId" class="gamelab-card-wrapper">
              <div
                  class="test-card no-text-select"
                  :class="{
                   'selected': isGameLabSelected(item.gameLabsId),
                   'disabled-card': !item.canDelete
                  }"
                  @click="handleGameLabCardClick(item)"
              >


              <div
                    class="checkbox-wrapper"
                    :class="{'disabled': !item.canDelete}"
                    @click.stop="handleGameLabSelect(item.gameLabsId)"
                >
                  <img
                      v-if="!isGameLabSelected(item.gameLabsId)"
                      src="@/assets/images/checkbox-circle.svg"
                      alt="Select"
                      class="checkbox-icon"
                  />
                  <img
                      v-else
                      src="@/assets/images/checkboxchecked-circle.svg"
                      alt="Selected"
                      class="checkbox-icon"
                  />
                </div>

                <div class="status-tag" :class="item.isPublish ? 'Published' : 'Draft'">
                  <div class="status-dot" :class="item.isPublish ? 'Published' : 'Draft'"></div>
                  <span>{{item.isPublish ? $t('published') : $t('draft')}}</span>
                </div>

                <span class="test-date">{{formatDate(item.createdAt)}}</span>
                <span class="test-title">{{item.name}}</span>

                <div class="test-info">
                  <template v-if="item.startAt || item.finishAt">
                    <img src="@/assets/images/datepicker.svg" class="test-info-icon"/>
                    <span class="test-info-text">
                     {{ formatDate(item.startAt) || '-' }} - {{ formatDate(item.finishAt) || '-' }}
                    </span>
                    <div class="dot"></div>
                  </template>

                  <div class="test-time-row" v-if="item.finishAt && item.remaining && item.remaining.isPast === 0">
                    <img
                        src="@/assets/images/clockOutlineRed.svg"
                        class="test-info-icon"
                    />
                    <span class="test-info-text red">
                     {{ item.remaining.time }}
                     {{$t('time_is_up')}}
                    </span>
                  </div>

                  <div class="test-time-row" v-else-if="item.finishAt && item.remaining && item.remaining.isPast === 1">
                    <img
                        src="@/assets/images/clockOutlineGreen.svg"
                        class="test-info-icon"
                    />
                    <span class="test-info-text green">
                     {{ item.remaining.time }}
                    </span>
                  </div>

                  <div class="test-time-row" v-else-if="item.finishAt && item.remaining && item.remaining.isPast === 2">
                    <img
                        src="@/assets/images/clockOutlineYellow.svg"
                        class="test-info-icon"
                    />
                    <span class="test-info-text yellow">
                     {{ item.remaining.time }}
                    </span>
                  </div>
                </div>

                <div class="info-row">
                  <!--<span class="info-tag">{{item.compainesCount}} companies</span>-->
                  <!--<span class="info-tag">{{item.questionsCount}} questions</span>-->
                </div>
              </div>
            </div>
          </div>
          <div class="bottom-placement-pagination">
            <Pagination
                :current-page="gameLabsCurrentPage"
                :total-items="totalGameLabs"
                :per-page="9"
                :on-page-change="handleGameLabsPage"
            />
          </div>
        </div>
      </div>

      <div v-if="tabIndex===6" class="tab-container">

        <DepartmentsTable :loading="isLoading" :deps="deps" :company-id="details.companyId" :add-new-department="() => departmentsModalVisible = true" :edit-department="handleEdit" :delete-department="handleDeleteDepartment" />
        <div class="pagination-align-center">
          <Pagination
              :current-page="depsCurrentPage"
              :total-items="totalDeps"
              :per-page="depsPerPage"
              :on-page-change="handleDepsPage"
          />
        </div>
        <DepartmentModal :is-visible="departmentsModalVisible" :on-close="() => departmentsModalVisible = false" :refresh-callback="refreshData" :company-id="this.details.companyId" />
        <DepartmentModal :is-visible="departmentEditModalVisible" :on-close="() => departmentEditModalVisible = false" :edit-mode="true" :edit-data="editData" :refresh-callback="refreshData"/>
        <ConfirmDeleteModal
            :is-visible="confirmDeleteModalVisible"
            :on-close="onDepartmentDeleteCancel"
            message="User Group will be deleted permanently"
            :on-action="handleDeleteRequest"
        />
      </div>

      <div v-if="tabIndex === 7" class="tab-container">
        <h3>{{ $t('license_title') }}</h3>
        <LicenseTable
            :loading="licensesLoading"
            :licenses="licenses"
            :add-new-license="showLicenseModal"
            :edit-license="handleEditLicense"
            :delete-license="handleDeleteLicense"
        />
      </div>

      <div v-if="tabIndex === 8" class="tab-container">
        <h3>{{ $t('remained_usages') }}</h3>
        <div class="usage-section">
          <div class="usage-card-wrapper">
            <UsageCard
                :usage-items="companyUsageData"
                :loading="companyUsageLoading"
                :error="companyUsageError"
                :show-manage-button="true"
                :skeleton-count="5"
                @retry="handleCompanyUsageRetry"
                @manage="handleCompanyUsageManage"
            />
          </div>
        </div>
      </div>
    </div>

    <CompanyModal
        :edit-mode="true"
        :edit-data="details"
        :visible="editModalVisible"
        :on-hide="hideEditModal"
        :refresh-callback="getDetails"
    />
    <AssignProgramModal
        :visible="assignmentModal"
        :pre-data="details"
        type="company"
        :on-hide="hideAssignModal"
        :refresh-callback="getDetails"
    />
    <ConfirmDeleteModal
        :title="this.$t('alert_company_messages')"
        :message="this.$t('all_company_information_deleted')"
        :is-visible="deleteModalVisible"
        :on-action="handleDelete"
        :on-close="handleDeleteModalClose"
    />
    <AssignTestModal
        :visible="assignTestModal"
        :pre-data="details"
        type="company"
        :on-hide="hideAssignTestModal"
        :refresh-callback="getTestsPaginated"
    />
    <AssignGameLabsModal
        :visible="assignGameLabsModalVisible"
        :pre-data="details"
        type="company"
        :on-hide="hideAssignGameLabsModal"
        :refresh-callback="getGameLabsPaginated"
    />

    <LicenseModal
        :is-visible="licenseModalVisible"
        :on-close="hideLicenseModal"
        :refresh-callback="getLicensesPaginated"
        :company-id="details.companyId"
        :edit-mode="editMode"
        :edit-data="editData"
        :license="selectedLicense"
    />
  </Layout>
</template>
<script>
import moment from 'moment';
import 'moment/locale/tr';
import companyService from "@/service/companyService";
import LicenseModal from "@/components/modals/LicenseModal.vue";
import LicenseTable from "@/components/tables/LicenseTable.vue";
import Layout from "@/components/layouts/Layout.vue";
import CompanyHeader from "@/components/CompanyHeader.vue";
import ExpandableSearch from "@/components/ExpandableSearch.vue";
import UserTable from "@/components/tables/User/UserTable.vue";
import DepartmentsTable from "@/components/tables/Departments/DepartmentsTable.vue";
import ProgramCard from "@/components/ProgramCard.vue";
import TransactionTable from "@/components/tables/TransactionTable.vue";
import InviteUserModal from "@/components/modals/InviteUserModal.vue";
import GroupAssignModal from "@/components/modals/GroupAssignModal.vue";
import AssignProgramModal from "@/components/modals/AssignProgramModal.vue";
import programService from "@/service/programService";
import userService from "@/service/userService";
import CompanyModal from "@/components/modals/CompanyModal.vue";
import ConfirmDeleteModal from "@/components/modals/ConfirmDeleteModal.vue";
import AssignTestModal from "@/components/modals/AssignTestModal.vue";
import AssignGameLabsModal from "@/components/modals/AssignGameLabsModal.vue";
import {handleErrorResponse} from "@/utils/utils";
import depService from "@/service/depService";
import DepartmentModal from "@/components/modals/DepartmentModal.vue";
import roleService from "@/service/roleService";
import testService from "@/service/testService";
import gameLabService from "@/service/gameLabService";
import TableDownload from "@/components/TableDownload.vue";
import Pagination from "@/components/Pagination.vue";
import SelectBox from "@/components/SelectBox.vue";
import Calendly from "@/pages/Calendly.vue";
import UsageCard from '@/components/UsageCard.vue'
import pricingService from '@/service/pricingService'

export default {
  components: {
    Calendly,
    SelectBox,
    Pagination,
    TableDownload,
    DepartmentModal,
    DepartmentsTable,
    AssignGameLabsModal,
    AssignTestModal,
    ConfirmDeleteModal,
    CompanyModal,
    AssignProgramModal,
    InviteUserModal,
    GroupAssignModal,
    TransactionTable,
    ProgramCard,
    UserTable,
    ExpandableSearch,
    CompanyHeader,
    Layout,
    LicenseModal,
    LicenseTable,
    UsageCard
  },
  data() {
    return {
      companyId: null,
      departmentsModalVisible: false,
      isLoading: true,
      searchText: '',
      tabIndex: 0,
      editModalVisible: false,
      deleteModalVisible: false,
      addUserModal: false,
      groupAssignModal: false,
      assignmentModal: false,
      assignTestModal: false,
      assignGameLabsModalVisible: false,
      departmentEditModalVisible: false,
      deleteId: null,
      confirmDeleteModalVisible: false,
      details: {},
      roleList: [],
      deps: [],
      dataLoaded: false,
      selectedPrograms: [],
      deleteSure: false,
      deleteTimer: null,
      selectedTests: [],
      deleteTestSure: false,
      deleteTestTimer: null,
      selectedGameLabs: [],
      deleteGameLabSure: false,
      deleteGameLabTimer: null,
      usersCurrentPage: 1,
      depsCurrentPage: 1,
      programsCurrentPage: 1,
      testsCurrentPage: 1,
      gameLabsCurrentPage: 1,
      totalDeps: 0,
      totalUsers: 0,
      totalPrograms: 0,
      totalTests: 0,
      totalGameLabs: 0,
      depsPerPage: 20,
      usersPerPage: 20,
      programsPerPage: 9,
      testsPerPage: 9,
      gameLabsPerPage: 9,
      depsLoading: false,
      usersLoading: false,
      programsLoading: false,
      testsLoading: false,
      gameLabsLoading: false,
      searchTimeout: null,
      tableUsers: [],
      programList: [],
      testList: [],
      gameLabsList: [],
      firstLoad: true,
      licenses: [],
      licensesLoading: false,
      licenseModalVisible: false,
      licensesCurrentPage: 1,
      totalLicenses: 0,
      licensesPerPage: 20,
      editMode: false,
      editData: {},
      selectedLicense: null,
      companyUsageData: [],
      companyUsageLoading: false,
      companyUsageError: null,
      allowedTabs:{}
    }
  },

  computed: {
    currentLocale() {
      return localStorage.getItem('userLanguage') || this.$i18n.locale || 'tr';
    },

    localeForDate() {
      const localeMap = {
        'tr': 'tr-TR',
        'en': 'en-US'
      };
      return localeMap[this.currentLocale] || 'tr-TR';
    }
  },

  mounted() {
    moment.locale(this.currentLocale === 'en' ? 'en' : 'tr');
    this.loadAllData();
  },

  methods: {
    async loadAllData() {
      try {
        if(this.firstLoad){
          this.firstLoad = false;
          await Promise.all([
            this.getDetails(),
            this.loadAllowedTabs(),
          ]);
        }
        this.checkUrlParams();

        if (this.tabIndex === 8) {
          await this.getCompanyUsageData();
        }
        this.dataLoaded = true;
      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
      }
    },

    async loadAllowedTabs() {
      try {
        const response = await roleService.getMenuModules();
        this.allowedTabs = response.data.data.find(item => item.key === 'companies').tabPermissions;
      } catch (error) {
        console.error('Failed to load allowed tabs:', error);
      }
    },

    checkUrlParams() {
      const addUserModalParam = this.$route.query.addUserModalTrue;
      const assignModalParam = this.$route.query.assignModalTrue;
      const tabParam = this.$route.query.tab;

      if (tabParam !== undefined && tabParam !== null) {
        const tabValue = parseInt(tabParam, 10);
        if (!isNaN(tabValue) && [0, 1, 2, 3, 5, 6, 7, 8].includes(tabValue)) {
          this.tabIndex = tabValue;
          this.loadTabData(tabValue);
        }
      }

      if (addUserModalParam === 'true') {
        this.tabIndex = 1;
        this.$nextTick(() => {
          this.addUserModal = true;
        });
      }

      if (assignModalParam === 'true') {
        this.tabIndex = 2;
        this.$nextTick(() => {
          this.assignmentModal = true;
        });
      }
    },

    loadTabData(index) {
      if (index === 1) {
        this.getDepsPaginated();
        this.getUsersPaginated();
      } else if (index === 2) {
        this.getProgramsPaginated();
      } else if (index === 3) {
        this.getTestsPaginated();
      } else if (index === 5) {
        this.getGameLabsPaginated();
      } else if (index === 6) {
        this.getDepsPaginated();
      } else if (index === 7) {
        this.getLicensesPaginated();
      } else if (index === 8) {
        this.getCompanyUsageData();
      }
    },

    deleteProgramAssign(programId) {
      if (!confirm(this.$t('confirm_delete_program_assign') || 'Bu program atamasını silmek istediğinize emin misiniz?')) {
        return;
      }

      const deleteData = {
        programIds: [programId],
        companyId: this.details.companyId
      };

      programService.deleteProgramAssign(deleteData).then(() => {
        this.$snackbar.add({
          type: 'success',
          text: this.$t('program_assignment_deleted')
        });
        this.getDetails();
      }).catch(error => {
        handleErrorResponse(error, this.$snackbar);
      });
    },

    handleDeleteRequest() {
      if (!this.deleteId) {
        return;
      }

      this.confirmDeleteModalVisible = false;
      this.isLoading = true;

      depService.deleteDep(this.deleteId).then(response => {
        this.$snackbar.add({
          type: 'success',
          text: this.$t('department_deleted_successfully')
        });
        this.isLoading = false;
        this.deleteId = null;
        this.refreshData();
      }).catch(error => {
        handleErrorResponse(error, this.$snackbar);
        this.isLoading = false;
      });
    },

    onDepartmentDeleteCancel() {
      this.confirmDeleteModalVisible = false;
      this.deleteId = null;
    },

    handleEdit(data) {
      this.editData = data;
      this.departmentEditModalVisible = true;
    },

    handleDeleteDepartment(depId) {
      event.stopPropagation();
      this.deleteId = depId;
      this.confirmDeleteModalVisible = true;
    },

    handleSearch(e) {
      this.searchText = e?.target?.value || undefined;

      if (this.tabIndex === 1) {
        this.usersCurrentPage = 1;
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.getUsersPaginated();
        }, 500);
      }

      if (this.tabIndex === 2) {
        this.programsCurrentPage = 1;
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.getProgramsPaginated();
        }, 500);
      }

      if (this.tabIndex === 3) {
        this.testsCurrentPage = 1;
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.getTestsPaginated();
        }, 500);
      }

      if (this.tabIndex === 5) {
        this.gameLabsCurrentPage = 1;
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.getGameLabsPaginated();
        }, 500);
      }

      if (this.tabIndex === 6) {
        this.depsCurrentPage = 1;
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.getDepsPaginated();
        }, 500);
      }

      if (this.tabIndex === 7) {
        this.licensesCurrentPage = 1;
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.getLicensesPaginated();
        }, 500);
      }

      if (this.tabIndex === 8) {
        this.getCompanyUsageData();
      }
    },

    changeTab(index) {
      if (this.isLoading) return;

      this.tabIndex = index;

      const query = { ...this.$route.query, tab: index.toString() };
      this.$router.replace({ query }).catch(() => {});

      if (index === 1) {
        this.depsCurrentPage = 1;
        this.depsLoading = false;
        this.isLoading = false;
        this.getDepsPaginated();
        this.usersCurrentPage = 1;
        this.usersLoading = false;
        this.isLoading = false;
        this.getUsersPaginated();
      }

      if (index === 2) {
        this.programsCurrentPage = 1;
        this.programsLoading = false;
        this.isLoading = false;
        this.getProgramsPaginated();
      }

      if (index === 3) {
        this.testsCurrentPage = 1;
        this.testsLoading = false;
        this.isLoading = false;
        this.getTestsPaginated();
      }

      if (index === 5) {
        this.gameLabsCurrentPage = 1;
        this.gameLabsLoading = false;
        this.isLoading = false;
        this.getGameLabsPaginated();
      }

      if (index === 6) {
        this.depsCurrentPage = 1;
        this.depsLoading = false;
        this.isLoading = false;
        this.getDepsPaginated();
      }

      if (index === 7) {
        this.licensesCurrentPage = 1;
        this.licensesLoading = false;
        this.isLoading = false;
        this.getLicensesPaginated();
      }

      if (index === 8) {
        this.getCompanyUsageData();
      }
    },

    showEditModal() {
      this.editModalVisible = true;
    },

    showDeleteModal() {
      this.deleteModalVisible = true;
      event.stopPropagation();
    },

    hideEditModal() {
      this.editModalVisible = false;
    },

    showInviteUser() {
      if (!this.deps || this.deps.length === 0) {
        this.$snackbar.add({
          text: this.$t('please_add_user_group_first'),
          type: 'warning',
        });
      }
      this.addUserModal = true;
    },

    hideInviteUser() {
      this.addUserModal = false;

      if (this.tabIndex === 1) {
        this.getUsersPaginated();
      }
    },

    showGroupAssignModal() {
      this.groupAssignModal = true;
    },

    hideGroupAssignModal() {
      this.groupAssignModal = false;
    },

    openAssignModal() {
      this.assignmentModal = true;
    },

    hideAssignModal() {
      this.assignmentModal = false;
    },

    showAssignTestModal() {
      this.assignTestModal = true;
    },

    hideAssignTestModal() {
      this.assignTestModal = false;
    },

    showAssignGameLabsModal() {
      this.assignGameLabsModalVisible = true;
    },

    hideAssignGameLabsModal() {
      this.assignGameLabsModalVisible = false;
    },

    showDepModal() {
      this.departmentsModalVisible = true;
    },

    getDetails() {
      const routeParams = this.$route.params;
      this.isLoading = true;

      companyService.getCompanyDetails(routeParams.id)
          .then((response) => {
            this.details = response.data.data;

            if (this.tabIndex === 1) {
              this.getUsersPaginated();
            } else {
              this.totalUsers = this.details.users?.length || 0;
            }

            if (this.tabIndex === 2) {
              this.getProgramsPaginated();
            } else {
              this.totalPrograms = this.details.programs?.length || 0;
            }

            if (this.tabIndex === 3) {
              this.getTestsPaginated();
            } else {
              this.totalTests = this.details.tests?.length || 0;
            }

            if (this.tabIndex === 5) {
              this.getGameLabsPaginated();
            } else {
              this.totalGameLabs = this.details.gameLabs?.length || 0;
            }

            if (this.tabIndex === 6) {
              this.getDepsPaginated();
            } else {
              this.totalDeps = this.details.deps?.length || 0;
            }

            if (this.tabIndex === 7) {
              this.getLicensesPaginated();
            } else {
              this.totalLicenses = this.details.licenses?.length || 0;
            }

            this.isLoading = false;

            roleService.getStoreByCompanyRole(routeParams.id).then(response => {
              this.roleList = response.data.data;
            }).catch(error => {
              handleErrorResponse(error, this.$snackbar);
            });
          })
          .catch(err => {
            handleErrorResponse(err, this.$snackbar);
          });
    },

    refreshData() {
      if (this.tabIndex === 6) {
        this.getDepsPaginated();
      } else if (this.tabIndex === 7) {
        this.getLicensesPaginated();
      } else if (this.tabIndex === 8) {
        this.getCompanyUsageData();
      }
    },

    formatDate(dateStr) {
      if (!dateStr || dateStr === null || dateStr === undefined) {
        return '';
      }

      try {
        const date = new Date(dateStr);

        if (isNaN(date.getTime())) {
          return '';
        }

        const currentLang = this.currentLocale === 'en' ? 'en-US' : 'tr-TR';

        const formatter = new Intl.DateTimeFormat(currentLang, {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        });

        return formatter.format(date);
      } catch (e) {
        return '';
      }
    },

    handleDeleteModalClose() {
      this.deleteModalVisible = false;
    },

    handleDelete() {
      this.deleteModalVisible = false;

      companyService.deleteCompany(this.details.companyId).then(() => {
        this.$router.push('/companies');
      }).catch(() => {
        this.$snackbar.add({
          text: this.$t('company_delete_error'),
          type: 'error',
        });
      })
    },

    handleProgramSelect(programId) {
      const program = this.programList.find(p => p.programId === programId);

      if (program && !program.canDelete) {
        const assignedType = program.assignedType || program.assignType || 'unknown';
        const displayType = this.$t(`assign_type_${assignedType}`);

        let messageKey = '';
        if (assignedType === 'user') {
          messageKey = 'program_assigned_from_user';
        } else if (assignedType === 'department') {
          messageKey = 'program_assigned_from_department';
        } else {
          messageKey = 'program_assigned_from_other';
        }

        this.$snackbar.add({
          type: 'warning',
          text: this.$t(messageKey, { type: displayType }),
          timeout: 4000
        });
        return;
      }

      if (this.selectedPrograms.includes(programId)) {
        this.selectedPrograms = this.selectedPrograms.filter(id => id !== programId);
      } else {
        this.selectedPrograms.push(programId);
      }
    },

    handleProgramCardClick(program) {
      const assignedType = program.assignedType || program.assignType || 'company';

      if (assignedType !== 'company') {
        const displayType = this.$t(`assign_type_${assignedType}`);

        let messageKey = '';
        if (assignedType === 'user') {
          messageKey = 'program_assigned_from_user';
        } else if (assignedType === 'department') {
          messageKey = 'program_assigned_from_department';
        } else {
          messageKey = 'program_assigned_from_other';
        }

        this.$snackbar.add({
          type: 'warning',
          text: this.$t(messageKey, { type: displayType }),
          timeout: 4000
        });
        return;
      }
    },

    isProgramSelected(programId) {
      return this.selectedPrograms.includes(programId);
    },

    handleMultipleDelete() {
      if (this.selectedPrograms.length === 0) {
        this.$snackbar.add({
          type: 'warning',
          text: this.$t('select_programs_to_delete')
        });
        return;
      }

      if (this.deleteSure) {
        clearTimeout(this.deleteTimer);

        const deleteData = {
          programIds: this.selectedPrograms,
          companyId: this.details.companyId
        };

        programService.deleteProgramAssign(deleteData).then(() => {
          this.$snackbar.add({
            type: 'success',
            text: this.$t('programs_unassigned_successfully', { count: this.selectedPrograms.length })
          });
          this.selectedPrograms = [];
          this.deleteSure = false;
          this.getDetails();
        }).catch(error => {
          handleErrorResponse(error, this.$snackbar);
        });
      } else {
        this.deleteSure = true;

        this.deleteTimer = setTimeout(() => {
          this.deleteSure = false;
        }, 3000);
      }
    },

    goToTest(testId) {
      this.$router.push(`/test/${testId}`);
    },

    handleTestSelect(testId) {
      const test = this.testList.find(t => t.testId === testId);

      if (test && !test.canDelete) {
        const assignedType = test.assignedType || test.assignType || 'unknown';
        const displayType = this.$t(`assign_type_${assignedType}`);

        let messageKey = '';
        if (assignedType === 'user') {
          messageKey = 'test_assigned_from_user';
        } else if (assignedType === 'department') {
          messageKey = 'test_assigned_from_department';
        } else {
          messageKey = 'test_assigned_from_other';
        }

        this.$snackbar.add({
          type: 'warning',
          text: this.$t(messageKey, { type: displayType }),
          timeout: 4000
        });
        return;
      }

      if (this.selectedTests.includes(testId)) {
        this.selectedTests = this.selectedTests.filter(id => id !== testId);
      } else {
        this.selectedTests.push(testId);
      }
    },

    handleTestCardClick(test) {
      const assignedType = test.assignedType || test.assignType || 'company';

      if (assignedType !== 'company') {
        const displayType = this.$t(`assign_type_${assignedType}`);

        let messageKey = '';
        if (assignedType === 'user') {
          messageKey = 'test_assigned_from_user';
        } else if (assignedType === 'department') {
          messageKey = 'test_assigned_from_department';
        } else {
          messageKey = 'test_assigned_from_other';
        }

        this.$snackbar.add({
          type: 'warning',
          text: this.$t(messageKey, { type: displayType }),
          timeout: 4000
        });
        return;
      }

      // this.goToTest(test.testId);
    },

    isTestSelected(testId) {
      return this.selectedTests.includes(testId);
    },

    handleMultipleTestDelete() {
      if (this.selectedTests.length === 0) {
        this.$snackbar.add({
          type: 'warning',
          text: this.$t('select_tests_to_delete')
        });
        return;
      }

      if (this.deleteTestSure) {
        clearTimeout(this.deleteTestTimer);

        const deleteData = {
          testIds: this.selectedTests,
          companyId: this.details.companyId
        };

        testService.deleteTestAssign(deleteData).then(() => {
          this.$snackbar.add({
            type: 'success',
            text: this.$t('test_assignments_deleted', { count: this.selectedTests.length })
          });
          this.selectedTests = [];
          this.deleteTestSure = false;
          this.getDetails();
        }).catch(error => {
          handleErrorResponse(error, this.$snackbar);
          this.deleteTestSure = false;
        });
      } else {
        this.deleteTestSure = true;

        this.deleteTestTimer = setTimeout(() => {
          this.deleteTestSure = false;
        }, 3000);
      }
    },

    goToGameLab(gameLabId) {
      this.$router.push(`/game/${gameLabId}`);
    },

    handleGameLabSelect(gameLabId) {
      const gameLab = this.gameLabsList.find(g => g.gameLabsId === gameLabId);

      if (gameLab && !gameLab.canDelete) {
        const assignedType = gameLab.assignedType || gameLab.assignType || 'unknown';
        const displayType = this.$t(`assign_type_${assignedType}`);

        let messageKey = '';
        if (assignedType === 'user') {
          messageKey = 'gamelab_assigned_from_user';
        } else if (assignedType === 'department') {
          messageKey = 'gamelab_assigned_from_department';
        } else {
          messageKey = 'gamelab_assigned_from_other';
        }

        this.$snackbar.add({
          type: 'warning',
          text: this.$t(messageKey, { type: displayType }),
          timeout: 4000
        });
        return;
      }

      if (this.selectedGameLabs.includes(gameLabId)) {
        this.selectedGameLabs = this.selectedGameLabs.filter(id => id !== gameLabId);
      } else {
        this.selectedGameLabs.push(gameLabId);
      }
    },

    handleGameLabCardClick(gameLab) {
      const assignedType = gameLab.assignedType || gameLab.assignType || 'company';

      if (assignedType !== 'company') {
        const displayType = this.$t(`assign_type_${assignedType}`);

        let messageKey = '';
        if (assignedType === 'user') {
          messageKey = 'gamelab_assigned_from_user';
        } else if (assignedType === 'department') {
          messageKey = 'gamelab_assigned_from_department';
        } else {
          messageKey = 'gamelab_assigned_from_other';
        }

        this.$snackbar.add({
          type: 'warning',
          text: this.$t(messageKey, { type: displayType }),
          timeout: 4000
        });
        return;
      }

      // this.goToGameLab(gameLab.gameLabsId);
    },

    isGameLabSelected(gameLabId) {
      return this.selectedGameLabs.includes(gameLabId);
    },

    handleMultipleGameLabDelete() {
      if (this.selectedGameLabs.length === 0) {
        this.$snackbar.add({
          type: 'warning',
          text: this.$t('select_gamelabs_to_delete')
        });
        return;
      }

      if (this.deleteGameLabSure) {
        clearTimeout(this.deleteGameLabTimer);

        const deleteData = {
          gameLabsIds: this.selectedGameLabs,
          companyId: this.details.companyId
        };

        gameLabService.deleteGameLabAssign(deleteData).then(() => {
          this.$snackbar.add({
            type: 'success',
            text: this.$t('gamelab_assignments_deleted', { count: this.selectedGameLabs.length })
          });
          this.selectedGameLabs = [];
          this.deleteGameLabSure = false;
          this.getDetails();
        }).catch(error => {
          handleErrorResponse(error, this.$snackbar);
          this.deleteGameLabSure = false;
        });
      } else {
        this.deleteGameLabSure = true;

        this.deleteGameLabTimer = setTimeout(() => {
          this.deleteGameLabSure = false;
        }, 3000);
      }
    },

    beforeUnmount() {
      if (this.deleteTimer) {
        clearTimeout(this.deleteTimer);
      }
      if (this.deleteTestTimer) {
        clearTimeout(this.deleteTestTimer);
      }
      if (this.deleteGameLabTimer) {
        clearTimeout(this.deleteGameLabTimer);
      }
    },

    handleUsersPage(page) {
      if (this.usersLoading) return;
      this.usersCurrentPage = page;
      this.usersLoading = true;
      this.getUsersPaginated();
    },

    handleProgramsPage(page) {
      if (this.programsLoading) return;
      this.programsCurrentPage = page;
      this.getProgramsPaginated();
    },

    handleTestsPage(page) {
      if (this.testsLoading) return;
      this.testsCurrentPage = page;
      this.getTestsPaginated();
    },

    handleGameLabsPage(page) {
      if (this.gameLabsLoading) return;
      this.gameLabsCurrentPage = page;
      this.getGameLabsPaginated();
    },

    handleDepsPage(page) {
      if (this.depsLoading) return;
      this.depsCurrentPage = page;
      this.getDepsPaginated();
    },

    handleLicensesPage(page) {
      if (this.licensesLoading) return;
      this.licensesCurrentPage = page;
      this.licensesLoading = true;
      this.getLicensesPaginated();
    },

    async getProgramsPaginated() {
      this.programsLoading = true;
      try {
        const routeParams = this.$route.params;

        const response = await programService.getCompanyPrograms(
            this.programsCurrentPage,
            routeParams.id,
            [],
            this.searchText || '',
            this.programsPerPage
        );

        if (response?.data?.responseType === 'success' && response.data.data) {
          this.programList = response.data.data.data.map(program => ({
            ...program,
            canDelete: (program.assignedType || program.assignType || 'company') === 'company'
          }));
          this.totalPrograms = response.data.data.total || 0;
        } else {
          this.programList = [];
          this.totalPrograms = 0;
        }

      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
        this.programList = [];
        this.totalPrograms = 0;
      }
      this.programsLoading = false;
    },

    async getTestsPaginated() {
      this.testsLoading = true;
      try {
        const routeParams = this.$route.params;

        const response = await testService.getCompanyTests(
            this.testsCurrentPage,
            routeParams.id,
            [],
            this.searchText || '',
            this.testsPerPage,
            'test'
        );

        if (response?.data?.responseType === 'success' && response.data.data) {
          this.testList = response.data.data.data.map(test => ({
            ...test,
            canDelete: (test.assignedType || test.assignType || 'company') === 'company'
          }));
          this.totalTests = response.data.data.total || 0;
        } else {
          this.testList = [];
          this.totalTests = 0;
        }

      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
        this.testList = [];
        this.totalTests = 0;
      }
      this.testsLoading = false;
    },

    async getGameLabsPaginated() {
      this.gameLabsLoading = true;
      try {
        const routeParams = this.$route.params;

        const response = await gameLabService.getCompanyGameLabs(
            this.gameLabsCurrentPage,
            routeParams.id,
            [],
            this.searchText || '',
            this.gameLabsPerPage,
            'gameLabs'
        );

        if (response?.data?.responseType === 'success' && response.data.data) {
          this.gameLabsList = response.data.data.data.map(item => ({
            ...item,
            remaining: item.remaining || { isPast: 0, time: '' },
            canDelete: (item.assignedType || item.assignType || 'company') === 'company'
          }));
          this.totalGameLabs = response.data.data.total || 0;
        } else {
          this.gameLabsList = [];
          this.totalGameLabs = 0;
        }

      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
        this.gameLabsList = [];
        this.totalGameLabs = 0;
      }
      this.gameLabsLoading = false;
    },

    async getDepsPaginated() {
      this.depsLoading = true;
      try {
        const routeParams = this.$route.params;

        const response = await depService.getCompanyDeps(
            this.depsCurrentPage,
            routeParams.id,
            [],
            this.searchText || '',
            this.depsPerPage,
        );

        if (response?.data?.responseType === 'success' && response.data.data) {
          this.deps = response.data.data.data;
          this.totalDeps = response.data.data.total || 0;
        } else {
          this.deps = [];
          this.totalDeps = 0;
        }

      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
        this.deps = [];
        this.totalDeps = 0;
      }
      this.depsLoading = false;
    },

    async getLicensesPaginated() {
      this.licensesLoading = true;
      try {
        const routeParams = this.$route.params;

        const response = await companyService.getCompanyLicenses(routeParams.id);

        if (response?.data?.responseType === 'success' && response.data.data) {
          const transformedLicenses = response.data.data.map(license => ({
            companyPackageId: license.companyPackageId,
            packageName: license.packageName,
            status: license.status,
            startDate: license.startDate,
            endDate: license.endDate,
            cancelledAt: license.cancelledAt || null,
            remainingDays: this.calculateRemainingDays(license.endDate),
            notes: license.notes || ''
          }));
          this.licenses = transformedLicenses;
          this.totalLicenses = response.data.data.length || 0;
        } else {
          this.licenses = [];
          this.totalLicenses = 0;
        }

      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
        this.licenses = [];
        this.totalLicenses = 0;
      }
      this.licensesLoading = false;
    },

    calculateRemainingDays(endDate) {
      if (!endDate || endDate === null || endDate === undefined) {
        return null;
      }

      try {
        const end = new Date(endDate.split('-').reverse().join('-'));
        const today = new Date();
        const diffTime = end - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
      } catch (e) {
        return null;
      }
    },

    async getUsersPaginated() {
      this.usersLoading = true;

      try {
        const routeParams = this.$route.params;

        const response = await userService.getCompanyUsers(
            this.usersCurrentPage,
            routeParams.id,
            [],
            this.searchText || '',
            this.usersPerPage
        );

        if (response?.data?.responseType === 'success' && response.data.data?.store) {
          const storeData = response.data.data.store;
          const rawUsers = storeData.data || [];

          this.tableUsers = rawUsers.map(user => ({
            ...user,
            department: user.departments && user.departments.length > 0
                ? {
                  id: user.departments[0].departmentId,
                  name: user.departments[0].name
                }
                : null
          }));

          this.totalUsers = storeData.total || 0;
        } else {
          this.tableUsers = [];
          this.totalUsers = 0;
        }

      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
        this.tableUsers = [];
        this.totalUsers = 0;
      }
      this.usersLoading = false;
    },

    refreshUsersData() {
      if (this.tabIndex === 1) {
        this.getUsersPaginated();
      }
    },

    showLicenseModal() {
      this.editMode = false;
      this.editData = {};
      this.licenseModalVisible = true;
    },

    hideLicenseModal() {
      this.licenseModalVisible = false;
    },

    handleEditLicense(license) {
      this.editMode = true;
      this.editData = {
        companyPackageId: license.companyPackageId,
        packageName: license.packageName,
        status: license.status,
        startDate: license.startDate,
        endDate: license.endDate,
        basePrice: license.basePrice,
        notes: license.notes,
        products: license.products || []
      };

      this.licenseModalVisible = true;
    },

    async handleDeleteLicense(licenseId) {
      if (confirm(this.$t('Lisansı silmek istediğinize emin misiniz?'))) {
        await this.getLicensesPaginated();
      }
    },

    async getCompanyUsageData() {
      this.companyUsageLoading = true;
      this.companyUsageError = null;

      try {
        const routeParams = this.$route.params;

        const response = await pricingService.getCompanyUsageLimits(routeParams.id);

        if (response?.data?.responseType === 'success') {
          this.companyUsageData = this.formatUsageDataForComponent(response.data.data || []);
        } else {
          this.companyUsageData = [];
          this.companyUsageError = response?.data?.message || 'Kullanım verileri alınamadı';
        }

      } catch (error) {
        this.companyUsageError = this.$t('usage_data_load_error');
        this.companyUsageData = [];
        handleErrorResponse(error, this.$snackbar);
      } finally {
        this.companyUsageLoading = false;
      }
    },

    formatUsageDataForComponent(usageData) {
      if (!Array.isArray(usageData)) return [];

      return usageData.map(item => {
        const remainingLimit = item.remainingLimit !== undefined
            ? item.remainingLimit
            : Math.max(0, (item.assignedLimit || 0) - (item.usedLimit || 0));

        let unit = 'adet';
        if (item.displayText) {
          const parts = item.displayText.trim().split(' ');
          unit = parts.length > 1 ? parts.slice(1).join(' ') : item.limitType || 'adet';
        } else if (item.limitType) {
          unit = item.limitType;
        }

        return {
          productKey: item.productKey,
          displayName: item.name || item.displayName || item.productKey,
          unit: unit,
          assignedLimit: item.assignedLimit || 0,
          usedLimit: item.usedLimit || 0,
          remainingLimit: remainingLimit,
          limitType: item.limitType || 'monthly'
        };
      }).sort((a, b) => (a.productKey || '').localeCompare(b.productKey || ''));
    },

    handleCompanyUsageRetry() {
      this.getCompanyUsageData();
    },

    handleCompanyUsageManage() {
      this.$snackbar.add({
        type: 'info',
        text: this.$t('contact_admin_for_usage_limits'),
        timeout: 5000
      });
    },
    handleUserAdded() {
      if (this.tabIndex === 1) {
        this.getUsersPaginated();
      }
    }
  },
}
</script>

<style scoped>
.table-download-holder{
  display: flex;
  justify-content: end;
  padding: 10px 30px;
}
.tab-panel {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-direction: row;
}
.main-content {
  padding: 25px;
}
.tab-panel-row {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #F2F4F7;
  border-bottom: 1px solid #F2F4F7;
  padding: 10px 0;
  margin-bottom: 20px;
}
.tab-button {
  padding: 6px 10px;
  border-radius: 10px;
  border: none;
  outline: none;
  background-color: transparent;
  cursor: pointer;
}
.tab-button span {
  font-family: 'euclid_medium', sans-serif;
  font-size: 12px;
  color: #344054;
}
.tab-button.active, .tab-button:hover {
  background-color: #F2F4F7;
}
h3 {
  font-family: euclid_medium, sans-serif;
  font-size: 18px;
  color: #040C2D;
  padding: 0;
  margin-top: 20px;
  margin-bottom: 20px;
}
.company-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  min-width: 300px;
  column-gap: 50px;
}
.company-detail-row {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
}
.company-detail-icon {
  background-color: #F5F5F5;
  border-radius: 16px;
  height: 44px;
  width: 44px;
  display: flex;
  justify-content: center;
  align-items: center;
}
.company-detail-icon img {
  cursor: default;
  width: 20px;
  height: 20px;
}
.detail-title {
  font-family: euclid_medium, sans-serif;
  display: block;
  color: #667085;
  font-size: 12px;
}
.detail-text {
  font-family: euclid_medium, sans-serif;
  display: block;
  color: #101828;
  font-size: 14px;
}
.company-detail-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.company-details {
  display: flex;
  flex-direction: row;
  gap: 20px;
}

.programs-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
}

.action-row {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
}

.tests-grid {
  width: 100%;
  display: inline-grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.test-card-wrapper {
  cursor: pointer;
}

.test-card {
  position: relative;
  width: 100%;
  border: 1px solid #EAECF0;
  background-color: #FCFCFD;
  border-radius: 20px;
  padding: 15px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  cursor: pointer;
  transition: all 0.2s ease;
}

.test-card:hover {
  box-shadow: 0 4px 20px rgba(16, 24, 40, 0.1);
  transform: translateY(-2px);
}

.test-card.selected {
  border-color: #3DA33E;
  background-color: #f8fff8;
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(61, 163, 62, 0.15);
}

.gameLabs-grid {
  width: 100%;
  display: inline-grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.status-tag {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #040C2D;
  border-radius: 8px;
  padding: 3px 15px;
  margin-bottom: 10px;
  display: flex;
  gap: 5px;
  align-items: center;
}

.status-dot {
  height: 8px;
  width: 8px;
  border-radius: 50%;
  display: inline-block;
}

.status-tag.Draft {
  background-color: #FAD9C6;
}

.status-tag.Published {
  background-color: #DDFBDE;
}

.status-dot.Draft {
  background-color: #F09460;
}

.status-dot.Published {
  background-color: #3DA33E;
}

.test-date {
  font-family: euclid_medium, sans-serif;
  font-size: 10px;
  color: #667085;
  margin-top: 30px;
}

.test-title {
  font-family: euclid_medium, sans-serif;
  font-size: 24px;
  color: #040C2D;
  margin-bottom: 10px;
}

.info-row {
  display: flex;
  flex-direction: row;
  gap: 5px;
}

.info-tag {
  font-family: euclid_regular, sans-serif;
  font-size: 10px;
  color: #040C2D;
  border-radius: 8px;
  padding: 3px 15px;
  margin-bottom: 12px;
  display: flex;
  gap: 5px;
  align-items: center;
  background-color: #F2F4F7;
}

.test-info-text {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #667085;
}

.test-info-text.red {
  color: #ff2121;
}

.test-info-text.green {
  color: #088701;
}

.test-info-text.yellow {
  color: #f1c40f;
}

.dot {
  height: 4px;
  width: 4px;
  border-radius: 50%;
  background-color: #d0d5dd;
}

.test-info {
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: center;
  margin-bottom: 30px;
}

.test-info-icon {
  width: 24px;
  height: 24px;
}

.test-time-row {
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: center;
}

@media (max-width: 1024px) {
  .programs-row {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .programs-row {
    grid-template-columns: 1fr;
  }
}

.btn-group-assign{
  display: flex;
  align-items: center;
  gap: 8px;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  height: 36px;
  white-space: nowrap;
  transition: all .2s ease;
  flex-shrink: 0;
}

.btn-group-assign span{
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #fff;
}

.delete-button {
  padding: 8px 16px;
  border: 1px solid #ff4757;
  background-color: transparent;
  color: #ff4757;
  border-radius: 8px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  transition: all 0.2s ease;
}

.delete-button:hover {
  background-color: #ff4757;
  color: white;
}

.delete-button.sure {
  background-color: #ff4757;
  color: white;
}

.delete-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.delete-button:disabled:hover {
  background-color: transparent;
  color: #ff4757;
}

.checkbox-wrapper {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  transition: all 0.2s ease;
}

.checkbox-wrapper:hover {
  background-color: rgba(255, 255, 255, 1);
  transform: scale(1.1);
}

.checkbox-icon {
  width: 18px;
  height: 18px;
}

.no-tests-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.no-tests-image {
  width: 120px;
  height: 120px;
  margin-bottom: 20px;
}

.no-tests-text {
  font-family: euclid_medium, sans-serif;
  font-size: 18px;
  color: #040C2D;
  margin-bottom: 8px;
}

.no-tests-description {
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
  color: #667085;
}

.pagination-align-center {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 20px;
}
.program-page-container{
  display: flex;
  flex-direction: column;
}
.bottom-placement-pagination{
  display: flex;
  justify-content: center;
  margin-top: 30px;
}

.license-wrapper{
  display: flex;
  flex-direction: row;
  gap: 10px;
}

.license-holder-one {
  display: flex;
  flex-direction: column;
  gap: 20px;
  height: 100%;
  width: 50%;
  background-color: #ffffff;
  border: 1px solid #efefef;
  border-radius: 8px;
  padding: 30px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 5px;
}
.pricing-button {
  display: inline-flex;
  height: 40px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  color: #1D4ED8;
  border: 1px solid #1D4ED8;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;
}

.pricing-button:hover {
  background-color: #1D4ED8;
  color: #ffffff;
}

.calendar-wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  width: 100%;
}

.dropdown {
  position: relative;
  width: 100%;
  height: 45px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0 15px;
  background-color: #F9FAFB;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.dropdown:hover {
  border-color: #9CA3AF;
}

.dropdown span {
  color: #6B7280;
  font-size: 14px;
}
.license-holder-two {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 50%;
  background-color: #ffffff;
  border: 1px solid #efefef;
  border-radius: 8px;
  padding: 30px;
}

.products-title {
  font-family: euclid_medium, sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #E5E7EB;
}

.product-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
  flex: 1;
}

.product-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: #F9FAFB;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.product-item:hover {
  background-color: #F3F4F6;
  border-color: #D1D5DB;
}

.product-info {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.product-name {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

.product-unit {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #6366F1;
  font-weight: 400;
}

.product-quantity-input {
  width: 80px;
  height: 35px;
  padding: 0 10px;
  background-color: #ffffff;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  text-align: center;
  transition: border-color 0.2s ease;
}

.product-quantity-input:focus {
  outline: none;
  border-color: #1D4ED8;
  box-shadow: 0 0 0 3px rgba(29, 78, 216, 0.1);
}

.footer-note {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #E5E7EB;
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #6B7280;
  line-height: 1.4;
}
.form-input {
  width: 100%;
  height: 45px;
  padding: 0 15px;
  background-color: #F9FAFB;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  transition: border-color 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: #1D4ED8;
  box-shadow: 0 0 0 3px rgba(29, 78, 216, 0.1);
}

.form-textarea {
  width: 100%;
  min-height: 120px;
  padding: 15px;
  background-color: #F9FAFB;
  border: 1px solid #D1D5DB;
  border-radius: 6px;
  font-size: 14px;
  color: #374151;
  font-family: euclid_medium, sans-serif;
  resize: vertical;
  transition: border-color 0.2s ease;
}

.form-textarea:focus {
  outline: none;
  border-color: #1D4ED8;
  box-shadow: 0 0 0 3px rgba(29, 78, 216, 0.1);
}

.date-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

@media (max-width: 768px) {
  .calendar-wrapper {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .license-holder-one {
    padding: 20px;
    gap: 15px;
  }
}

.disabled-card {
  opacity: 0.7;
}

.checkbox-wrapper.disabled {
  cursor: not-allowed !important;
  opacity: 0.5;
}

.checkbox-wrapper.disabled:hover {
  transform: none !important;
}
</style>
